import React from 'react';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

import { formControlClass } from '../../utils/formControls';

export const inputClass = formControlClass;

/** Flat in-cell input for Excel-style voucher grids */
export const excelCellInputClass =
  'w-full min-w-0 border-0 bg-transparent px-2 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/25';

export const labelClass = 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500';

export function AlertBanner({ type = 'error', message }) {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div
      className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-sm ${
        isError ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'
      }`}
    >
      {isError ? <AlertCircle className="h-4 w-4 flex-shrink-0" /> : <CheckCircle className="h-4 w-4 flex-shrink-0" />}
      {message}
    </div>
  );
}

export function SectionCard({ title, subtitle, action, children, className = '', bodyClassName = '' }) {
  return (
    <section className={`overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm ${className}`}>
      {(title || action) && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-gray-50/80 px-5 py-4">
          <div>
            {title ? <h2 className="text-sm font-semibold text-gray-900">{title}</h2> : null}
            {subtitle ? <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p> : null}
          </div>
          {action}
        </div>
      )}
      <div className={bodyClassName || 'p-5'}>{children}</div>
    </section>
  );
}

export function Field({ label, children, className = '' }) {
  return (
    <div className={className}>
      {label ? <label className={labelClass}>{label}</label> : null}
      {children}
    </div>
  );
}

export function StatCard({ label, value, hint }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-2 text-2xl font-bold text-gray-900">{value}</p>
      {hint ? <p className="mt-1 text-xs text-gray-500">{hint}</p> : null}
    </div>
  );
}

export function VoucherNoBadge({ value, loading, onRefresh, onDark = false }) {
  const metaClass = onDark ? 'text-white/70' : 'text-gray-500';
  const titleClass = onDark ? 'text-white/80' : 'text-gray-600';
  const linkClass = onDark ? 'text-white hover:text-white/90' : 'text-primary hover:underline';

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="inline-flex min-w-[140px] items-center justify-center rounded-xl bg-primary px-4 py-2.5 text-lg font-bold tracking-wide text-white shadow-md shadow-primary/20">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : value || '—'}
      </div>
      <div className="min-w-0">
        <p className={`text-xs font-semibold uppercase tracking-wide ${metaClass}`}>Voucher number</p>
        <p className={`text-sm ${titleClass}`}>Auto-assigned on save</p>
        {onRefresh ? (
          <button type="button" onClick={onRefresh} className={`mt-1 text-xs font-medium ${linkClass}`}>
            Refresh number
          </button>
        ) : null}
      </div>
    </div>
  );
}
